import React, { useState, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player';
import {
  Box,
  CircularProgress,
} from '@mui/material';

interface YouTubePreviewProps {
  url: string;
  width?: string | number;
  height?: string | number;
  showControls?: boolean;
  autoPlay?: boolean;
  onError?: (error: string) => void;
  onReady?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const YouTubePreview: React.FC<YouTubePreviewProps> = ({
  url,
  width = '100%',
  height = 200,
  showControls = false,
  autoPlay = false,
  onError: _onError,
  onReady,
  className,
  style,
}) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
  }, [url]);

  const handleReady = useCallback(() => {
    setIsLoading(false);
    onReady?.();
  }, [onReady]);

  const handleError = useCallback((_error: any) => {
    setIsLoading(false);
  }, []);

  const handleLoadStart = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleProgress = useCallback(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [isLoading]);

  const handlePlay = useCallback(() => {
    setIsLoading(false);
  }, []);

  return (
    <Box
      sx={{
        width,
        height,
        position: 'relative',
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: '#000',
        ...style,
      }}
      className={className}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: 'white' }} />
        </Box>
      )}

      <ReactPlayer
        url={url}
        width="100%"
        height="100%"
        controls={showControls}
        playing={autoPlay}
        onReady={handleReady}
        onError={handleError}
        onLoadStart={handleLoadStart}
        onProgress={handleProgress}
        onPlay={handlePlay}
        config={{
          youtube: {
            playerVars: {
              showinfo: 0,
              modestbranding: 1,
              rel: 0,
            },
          },
        }}
        style={{
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      />
    </Box>
  );
};

YouTubePreview.defaultProps = {
  width: '100%',
  height: 200,
  showControls: false,
  autoPlay: false,
  onError: undefined,
  onReady: undefined,
  className: undefined,
  style: undefined,
};

export default YouTubePreview;
